/* Auto-release schedule for the Restock pot: every N weeks from a start date, only when the pot covers the restock total. */
import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { CalendarClock, Loader2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => new Date(s + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export default function RestockPotSchedule({ pot, onChanged }) {
  const s = pot.auto_release;
  const [editing, setEditing] = useState(!s);
  const [weeks, setWeeks] = useState(s?.every_weeks || 4);
  const [start, setStart] = useState(s?.start_date || todayStr());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const call = async (fn) => {
    setBusy(true); setErr("");
    try { const { data } = await fn(); onChanged(data); setEditing(false); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="w-full mt-2 border-t border-cream/15 pt-3 text-xs" data-testid="restock-schedule">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-gold"><CalendarClock className="w-3.5 h-3.5" /> Pot release day · auto-release</div>
        {s && !editing && (
          <div className="flex items-center gap-3 text-cream/80">
            <span data-testid="restock-schedule-summary">Every {s.every_weeks} wk{s.every_weeks === 1 ? "" : "s"} · next <strong className="text-cream" data-testid="restock-next-date">{fmtDate(pot.next_release_date)}</strong></span>
            <button onClick={() => setEditing(true)} className="underline underline-offset-2 hover:text-cream" data-testid="restock-schedule-edit">Change</button>
            <button onClick={() => call(() => api.delete("/restock-pot/schedule"))} disabled={busy} className="underline underline-offset-2 hover:text-clay" data-testid="restock-schedule-off">Turn off</button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-2 flex-wrap text-cream/80">
          <span>Every</span>
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="bg-cream text-ink rounded-md px-2 py-1 text-xs" data-testid="restock-schedule-weeks">
            {[1, 2, 3, 4, 5, 6, 8, 12].map((w) => <option key={w} value={w}>{w} week{w === 1 ? "" : "s"}</option>)}
          </select>
          <span>starting</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-cream text-ink rounded-md px-2 py-1 text-xs font-mono" data-testid="restock-schedule-start" />
          <button onClick={() => call(() => api.post("/restock-pot/schedule", { every_weeks: weeks, start_date: start }))} disabled={busy || !start}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1 bg-gold text-ink font-medium hover:bg-gold-deep disabled:opacity-50" data-testid="restock-schedule-save">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} {s ? "Save" : "Set release day"}
          </button>
          {s && <button onClick={() => setEditing(false)} className="hover:text-cream" data-testid="restock-schedule-cancel">Cancel</button>}
        </div>
      ) : (
        <div className="mt-1.5 text-[11px] text-cream/60" data-testid="restock-schedule-note">
          On each release day the pot moves to Spending automatically — but only if it covers the full restock total; otherwise it waits for the next cycle.
          {pot.last_skipped && <span className="text-clay"> Skipped {fmtDate(pot.last_skipped.date)}: had ${pot.last_skipped.balance.toFixed(2)}, needed ${pot.last_skipped.needed.toFixed(2)}.</span>}
        </div>
      )}
      {err && <div className="mt-1 text-clay" data-testid="restock-schedule-error">{err}</div>}
    </div>
  );
}
